// ============================================================
// AYASA — basket rules shared by every way into the basket (Roy, 28-09)
// the product page, its lightbox buy bar, the shop page's Add to cart panel
// and the cart page's quantity fields.
//
// 1. At most 3 of the same model per order. Conditions are the same model:
//    E Amara 20, its -spotted and its -scratched count together.
// 2. Never show how much stock there is. Shopify answers an add beyond its
//    stock with 422 "Only 5 items were added…" AND quietly adds the 5, so the
//    basket is put back exactly as it was and only our own words show.
// ============================================================
window.AyasaCart = (() => {
  const MAX = 3;
  const MSG_LIMIT = AYASA_t("js.cart_rules.limit");
  const MSG_STOCK = AYASA_t("js.cart_rules.stock");
  const modelOf = handle => String(handle || "").replace(/-(scratched|spotted)$/, "");
  const isInstrument = i => i.product_type === "Handpan";

  async function call(url, body) {
    const r = await fetch(url, body ? { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) } : undefined);
    let data = null;
    try { data = await r.json(); } catch (e) { data = null; }
    return { ok: r.ok, status: r.status, data };
  }
  const getCart = async () => (await call("/cart.js")).data;
  const modelCount = (cart, handle) => cart.items
    .filter(i => isInstrument(i) && modelOf(i.handle) === modelOf(handle))
    .reduce((n, i) => n + i.quantity, 0);
  const fail = (message, code) => Object.assign(new Error(message), { code });

  // put the basket back to `before`: lines that appeared go, changed quantities return
  async function restore(before) {
    const after = await getCart();
    const was = Object.fromEntries(before.items.map(i => [i.key, i.quantity]));
    const updates = {};
    for (const i of after.items) if ((was[i.key] || 0) !== i.quantity) updates[i.key] = was[i.key] || 0;
    if (Object.keys(updates).length) await call("/cart/update.js", { updates });
  }

  // 3. Every bag knows its pan (Roy + Flow, 29-09). CONTRACT WITH FLOW: the
  //    hidden line property "_pan_id" (6 lowercase letters/digits) sits on an
  //    instrument line and on the bag line(s) bought for it; Flow ships a bag
  //    with its pan's part. An instrument line can hold several pans (the
  //    quantity box stays); its bags then share that line's id. The same
  //    instrument with the same t-shirt choice joins its existing line and id.
  //    The "_" keeps it out of the basket, checkout and emails. Message the
  //    orders chat before changing the key or its meaning.
  const PAN_KEY = "_pan_id";
  const newPanId = () => Array.from(crypto.getRandomValues(new Uint8Array(6)), b => "abcdefghijkmnpqrstuvwxyz23456789"[b % 32]).join("");
  const shown = props => JSON.stringify(Object.entries(props || {}).filter(([k]) => !k.startsWith("_")).sort());
  // the LAST item is the instrument, every other item is its bag
  function linkPan(items, cart) {
    const pan = items[items.length - 1];
    const line = cart.items.find(i => isInstrument(i) && i.variant_id === +pan.id && shown(i.properties) === shown(pan.properties));
    const id = (line && line.properties && line.properties[PAN_KEY]) || newPanId();
    return items.map(it => ({ ...it, properties: { ...(it.properties || {}), [PAN_KEY]: id } }));
  }

  // add(items, { instruments: [{ handle, quantity }], pan }) — throws Error with
  // .code "limit" or "stock" (message is the customer's text) or "other".
  // pan: true when items are one instrument (last) and its bag.
  async function add(items, { instruments = [], pan = false } = {}) {
    const before = await getCart();
    if (pan && items.length) items = linkPan(items, before);
    for (const ins of instruments) {
      const same = instruments.filter(x => modelOf(x.handle) === modelOf(ins.handle)).reduce((n, x) => n + (x.quantity || 1), 0);
      if (modelCount(before, ins.handle) + same > MAX) throw fail(MSG_LIMIT, "limit");
    }
    const r = await call("/cart/add.js", { items });
    if (r.ok) return r.data;
    if (r.status === 422) { try { await restore(before); } catch (e) {} throw fail(MSG_STOCK, "stock"); }
    throw fail(AYASA_t("js.cart_rules.add_failed"), "other");
  }

  // the cart page: apply { key: quantity } updates under the same two rules
  async function update(updates) {
    const before = await getCart();
    const next = before.items.map(i => ({ ...i, quantity: updates[i.key] != null ? +updates[i.key] : i.quantity }));
    const models = {};
    for (const i of next) if (isInstrument(i)) models[modelOf(i.handle)] = (models[modelOf(i.handle)] || 0) + i.quantity;
    if (Object.values(models).some(n => n > MAX)) throw fail(MSG_LIMIT, "limit");
    // /cart/update.js never checks stock (it takes any number), /cart/add.js
    // does — so lowering goes through update and raising adds the difference
    // (same variant and properties, so it lands on the same line)
    const lower = {}, raise = [];
    for (const [k, q] of Object.entries(updates)) {
      const line = before.items.find(i => i.key === k);
      if (!line || +q === line.quantity) continue;
      if (+q < line.quantity) lower[k] = +q;
      else raise.push({ id: line.variant_id, quantity: +q - line.quantity, properties: line.properties || {} });
    }
    const undo = async () => { try { await restore(before); } catch (e) {} };
    if (Object.keys(lower).length) {
      const r = await call("/cart/update.js", { updates: lower });
      if (!r.ok) { await undo(); throw fail(AYASA_t("js.cart_rules.update_failed"), "other"); }
    }
    if (raise.length) {
      const r = await call("/cart/add.js", { items: raise });
      if (!r.ok) { await undo(); throw r.status === 422 ? fail(MSG_STOCK, "stock") : fail(AYASA_t("js.cart_rules.update_failed"), "other"); }
    }
    return getCart();
  }

  return { MAX, MSG_LIMIT, MSG_STOCK, PAN_KEY, modelOf, getCart, modelCount, add, update };
})();
